import React from 'react';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import Translate from '../Translate/Translate.js';

import {
	IMorningstarStyleBoxConfiguration,
	IMorningstarStyleBoxProps,
	IMorningstarStyleBoxState,
} from './models/index.js';
import ActiveBox from './styledComponents/ActiveBox.js';
import BoxesWrapper from './styledComponents/BoxesWrapper.js';
import InactiveBox from './styledComponents/InactiveBox.js';
import MorningstarStyleBoxAxisLabel from './styledComponents/MorningstarStyleBoxAxisLabel.js';
import MorningstarStyleBoxTick from './styledComponents/MorningstarStyleBoxTick.js';
import MorningstarStyleBoxXAxis from './styledComponents/MorningstarStyleBoxXAxis.js';
import MorningstarStyleBoxYAxis from './styledComponents/MorningstarStyleBoxYAxis.js';
import Wrapper from './styledComponents/Wrapper.js';
import { MORNINGSTAR_STYLE_BOX_CATEGORY_KEY, MORNINGSTAR_STYLE_BOX_COMPONENT_KEY } from './MorningstarStyleBox.meta.js';

export class MorningstarStyleBox extends ComponentBase<
	IMorningstarStyleBoxProps,
	IMorningstarStyleBoxState,
	IMorningstarStyleBoxConfiguration
> {
	categoryKey = MORNINGSTAR_STYLE_BOX_CATEGORY_KEY;
	componentKey = MORNINGSTAR_STYLE_BOX_COMPONENT_KEY;

	constructor(props: IMorningstarStyleBoxProps) {
		super(props);

		makeObservable(this, {
			morningstarStyleBoxes: computed,
			yTicks: computed,
			xTicks: computed,
			accessibilityTitleText: computed,
		});
	}

	get morningstarStyleBoxes() {
		const { value } = this.props;
		const boxes = [];
		for (let i = 1; i <= 9; i++) {
			const key = `morningstar_style_box_${i}`;
			boxes.push(value === i ? <ActiveBox key={key} /> : <InactiveBox key={key} />);
		}
		return boxes;
	}

	get yTicks() {
		const { yTickLabels } = this.props;
		if (!yTickLabels) {
			return;
		}
		return yTickLabels.map((yTickLabel, index) => {
			return (
				<MorningstarStyleBoxTick key={`y_tick_${index}`}>
					<Translate>{yTickLabel}</Translate>
				</MorningstarStyleBoxTick>
			);
		});
	}

	get xTicks() {
		const { xTickLabels } = this.props;
		if (!xTickLabels) {
			return;
		}
		return xTickLabels.map((xTickLabel, index) => {
			return (
				<MorningstarStyleBoxTick key={`x_tick_${index}`}>
					<Translate>{xTickLabel}</Translate>
				</MorningstarStyleBoxTick>
			);
		});
	}

	get accessibilityTitleText() {
		const { yLabel, xLabel, yTickLabels, xTickLabels, value } = this.props;
		if (yLabel && xLabel && yTickLabels && xTickLabels) {
			const row = Math.ceil(value / 3) - 1;
			const col = (value - 1) % 3;

			return `${xLabel} ${xTickLabels[col]}. ${yLabel} ${yTickLabels[row]}`;
		}
		console.warn(
			`Morningstar style box: Insufficient data available to generate accessible text. Data received: ${JSON.stringify(
				{ yLabel, yTickLabels, xLabel, xTickLabels },
			)}`,
		);
		return;
	}

	render() {
		const { className, value, yLabel, xLabel } = this.props;
		if (!value) {
			return null;
		}
		return (
			<Wrapper className={className} title={this.accessibilityTitleText} role={'img'}>
				<BoxesWrapper>{this.morningstarStyleBoxes}</BoxesWrapper>
				<MorningstarStyleBoxXAxis>
					{this.xTicks}
					<MorningstarStyleBoxAxisLabel>
						<Translate>{xLabel}</Translate>
					</MorningstarStyleBoxAxisLabel>
				</MorningstarStyleBoxXAxis>
				<MorningstarStyleBoxYAxis>
					<MorningstarStyleBoxAxisLabel>
						<Translate>{yLabel}</Translate>
					</MorningstarStyleBoxAxisLabel>
					{this.yTicks}
				</MorningstarStyleBoxYAxis>
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(
	observer(MorningstarStyleBox),
	MORNINGSTAR_STYLE_BOX_CATEGORY_KEY,
	MORNINGSTAR_STYLE_BOX_COMPONENT_KEY,
);
